import {
  Box, Stack, useMediaQuery, Collapse, Tabs, Tab,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Languages, Calendar,
  AlertTriangle
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IDataRow, IGroupedData } from "./CalendarioObblighi.types";
import {
  activeRecipientData,
  checkPvr, getCurrentPvrHeader, HEADERS_LABEL, languagesString,
  sortByRecipients } from "./utils";
import { Spinner } from "../../components";
import { BadgeBox } from "../../components/Commons/BadgeBox";
import { CommonWidgetContainer } from "../../components/Commons/CommonWidgetContainer.styles";
import { TopicsItem } from "../../components/Commons/TopicsItem";
import { VerticalDivider } from "../../components/Divider/VerticalDivider";
import { useLanguageStore } from "../../stores";
import { TypographyExploreCardText } from "../../styles/Typography/TypographyExploreCardText.styles";
import { TypographyTextTag } from "../../styles/Typography/TypographyTextTag.styles";

export const RowList = () => {

  const { t } = useTranslation();

  const [showTable, setShowTable] = useState(false);
  const [formatError, setFormatError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tabsList, setTabsList] = useState<IGroupedData[]>([]);
  const [activeTab, setActiveTab] = useState<string>("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const language = useLanguageStore(state => state.language)?.id ?? "it";

  const loadCsvData = useCallback(async () => {
    try {
      setIsLoading(true);

      const baseFileName = import.meta.env.VITE_CALENDARIO_OBBLIGHI_URL ?? "";
      if (!baseFileName.endsWith(".csv")) {
        setFormatError(true);
        throw new Error(t("calendario_obblighi.invalid_csv_format"));
      }
      const normalizedLanguage = language?.toLowerCase();
      const resourceUrl = (normalizedLanguage && normalizedLanguage !== "it") ? baseFileName.replace(".csv", "_en.csv") : baseFileName;
      const response = await fetch(resourceUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Leggi il file come testo per CSV
      const text = await response.text();
      const lines = text.split("\n").filter(line => line.trim() !== "");

      // Parser CSV semplice per formato senza virgolette con separatore punto e virgola (;)
      const parsedData = lines.map(line => {
        return line.split(";").map(cell => cell.trim());
      });
      if(parsedData[0].length < 9) {
        setFormatError(true);
        throw new Error(t("calendario_obblighi.invalid_csv_format"));
      }

      const newOrder = sortByRecipients(parsedData);
      setTabsList(newOrder);

      if (newOrder.length > 0) {
        setActiveTab(newOrder[0].label);
        setShowTable(true);
      }

    } catch (error) {
      // Errore nel leggere il file CSV
      setTabsList([]);
    } finally{
      setIsLoading(false);
    }
  }, [language]);

  useEffect(() => {
    loadCsvData();
  }, [loadCsvData]);

  if(isLoading) return <Spinner />;

  if(formatError) return (
    <Stack  flexDirection="row" alignItems="center" justifyContent="center" gap="1rem">
      <AlertTriangle color={ theme.customColors.textWarning } />
      <Typography color={ theme.customColors.textWarning }>
        { t("calendario_obblighi.invalid_csv_format") }
      </Typography>
    </Stack>
  );

  if(tabsList.length === 0) return (
    <Stack  flexDirection="row" alignItems="center" justifyContent="center" gap="1rem">
      <AlertTriangle color={ theme.customColors.textWarning } />
      <Typography color={ theme.customColors.textWarning }>
        { t("calendario_obblighi.empty_state") }
      </Typography>
    </Stack>
  );

  return (
    <>
      { /* Tabelle con i dati */ }
      <Collapse in={ showTable } timeout="auto" unmountOnExit sx={ { width: "100%" } }>
        <Box sx={ { mt: 4 } }>
          { /* Tabs per i destinatari */ }
          <Box sx={ { borderColor: "divider", mb: 3 } }>
            <Tabs
              value={ activeTab }
              onChange={ (_, newValue) => setActiveTab(newValue) }
              variant={ "scrollable" }
              scrollButtons={ "auto" }
              TabIndicatorProps={ { style: { background: theme.customColors.accentVariantA, borderRadius: "1.5rem" } } }
              allowScrollButtonsMobile={ isMobile }
              sx={ {
                ".Mui-disabled.MuiTabs-scrollButtonsHideMobile": { opacity: "0.4 !important" },
                ".Mui-selected": { color: theme.customColors.accentVariantA },
                ".MuiTab-textColorPrimary:not(.Mui-selected)": { color:  theme.customColors.systemPrimary01 },
                fontFamily: "Inter"
              } }
            >
              { tabsList.map(({ label }) => (
                <Tab
                  key={ label + "-tab" }
                  label={ label }
                  value={ label }
                  sx={ {
                    "&.Mui-focusVisible": {
                      border: `1px solid ${theme.customColors.accentVariantA}`
                    },
                    borderBottom: `2px solid ${theme.customColors.border}`,
                    fontSize: "0.875rem",
                    fontStyle: "semibold",
                    fontWeight: 600,
                    height: "1.5rem",
                    letterSpacing: "0.0313em",
                    lineHeight: "1.5rem",
                    marginX:"0.35rem",
                    minWidth: "3rem",
                    textTransform: "none"
                  } }
                />
              )) }
            </Tabs>
          </Box>
          <Stack id="recipients-data-container" rowGap="0.5rem">
            { activeTab && activeRecipientData(tabsList, activeTab)?.map((rowData: IDataRow, index) => (
              <CommonWidgetContainer
                key={ index + activeTab }
                hoverBorderAccent={ false }
                width="unset !important"
                maxWidth="100% !important"
                sx={ {
                  gap:"0.75rem"
                } }
              >
                <Stack
                  className="header-info"
                  justifyContent={ "space-between" }
                  flexDirection={ !isMobile ? "row" : "column-reverse" }
                  gap="1rem"
                >
                  { /* LEFT INFOs */ }
                  <Box display="flex" flexDirection="row" gap="0.25rem" alignItems="center" sx={ { opacity: 0.7 } }>
                    <TypographyExploreCardText>
                      { rowData[HEADERS_LABEL.CODE_SEL] }
                    </TypographyExploreCardText>
                    <VerticalDivider />
                    <TypographyExploreCardText>
                      { rowData[HEADERS_LABEL.AMBITO] }
                    </TypographyExploreCardText>
                    <VerticalDivider />
                    <Languages size={ "1rem" } style={ {
                      position: "relative",
                      top:"20%"
                    } } />
                    { " " }
                    <TypographyExploreCardText>
                      { languagesString(rowData[HEADERS_LABEL.ITALIAN], rowData[HEADERS_LABEL.ENGLISH]) }
                    </TypographyExploreCardText>
                  </Box>
                  { /* RIGHT INFOs */ }
                  <Box display="flex" flexDirection="row" gap="0.25rem" alignItems="center" sx={ {
                    color:theme.customColors.textMandatory,
                    fontSize: "0.75rem",
                    fontWeight: 700
                  } }>
                    <Calendar size="0.85rem"/>
                    <TypographyTextTag sx={ {
                      color: theme.customColors.textMandatory
                    } }>
                      { rowData[HEADERS_LABEL.MONTH] }
                    </TypographyTextTag>
                    <TypographyTextTag sx={ {
                      color: theme.customColors.textMandatory
                    } }>
                      |
                    </TypographyTextTag>
                    <TypographyTextTag sx={ {
                      color: theme.customColors.textMandatory
                    } }>
                      { "Decade"  + " " + rowData[HEADERS_LABEL.DECADE] }
                    </TypographyTextTag>
                  </Box>
                </Stack>
                <Stack className="body-info">
                  { /* TITLE */ }
                  <TypographyExploreCardText fontWeight={ 700 } fontSize={ "1rem" } fontStyle="bold">
                    { rowData[HEADERS_LABEL.TITLE_SELECTION] }
                  </TypographyExploreCardText>
                </Stack>
                { /* TAGS AND BADGES */ }
                <Stack className="footer-info" flexDirection="row" gap="0.85rem">
                  <TopicsItem i={ 0 }>
                    <TypographyExploreCardText>
                      { rowData[HEADERS_LABEL.DESTINATARI] }
                    </TypographyExploreCardText>
                  </TopicsItem>
                  { checkPvr(rowData[getCurrentPvrHeader()]) && (
                    <BadgeBox
                      background={ theme.customColors.backgroundSecondary }
                      textColor={ theme.customColors.textTag }
                      border={ `1px solid ${theme.customColors.textTag}` }>
                      <TypographyTextTag>
                        { getCurrentPvrHeader() }
                      </TypographyTextTag>
                    </BadgeBox>
                  ) }
                </Stack>
              </CommonWidgetContainer>
            )) }
          </Stack>
        </Box>
      </Collapse>
    </>
  );
};
